import React from 'react';
import PublicLayout from '../components/PublicLayout';
import { useTranslation } from 'react-i18next';
import { FeaturesSEO } from '../components/SEO';
import { 
  MultiLanguageIcon, 
  QRCodeIcon, 
  CustomDesignIcon, 
  SmartStatsIcon,
  QuickSetupIcon,
  SyncIcon,
  FloatingShapes 
} from '../components/icons/FeatureIcons';
import { Upload, Puzzle } from 'lucide-react';

// Import/Export icon component matching FeatureIcons style
const ImportExportIcon = () => (
  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-200">
    <Upload className="w-8 h-8 text-white" />
  </div>
);

// Add-ons icon component
const AddonsIcon = () => (
  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-200">
    <Puzzle className="w-8 h-8 text-white" />
  </div>
);

const Features = () => {
  const { t } = useTranslation();
  const items = [
    {
      Icon: MultiLanguageIcon,
      title: t('pages.features.multiLanguage'),
      desc: t('pages.features.multiLanguageDesc')
    },
    {
      Icon: QRCodeIcon,
      title: t('pages.features.qrAccess'),
      desc: t('pages.features.qrAccessDesc')
    },
    {
      Icon: CustomDesignIcon,
      title: t('pages.features.themeCustomization'),
      desc: t('pages.features.themeCustomizationDesc')
    },
    {
      Icon: SmartStatsIcon,
      title: t('pages.features.currentStats'),
      desc: t('pages.features.currentStatsDesc')
    },
    {
      Icon: QuickSetupIcon,
      title: t('pages.features.easyManagement'),
      desc: t('pages.features.easyManagementDesc')
    },
    {
      Icon: SyncIcon,
      title: t('pages.features.secureFast'),
      desc: t('pages.features.secureFastDesc')
    },
    {
      Icon: ImportExportIcon,
      title: t('pages.features.importExport', 'Import / Export'),
      desc: t('pages.features.importExportDesc', 'Bulk import your menu from CSV or Excel files, export your data anytime, and keep your POS system in sync with external identifiers.')
    },
    {
      Icon: AddonsIcon,
      title: t('pages.features.addons', 'Pro Add-ons'),
      desc: t('pages.features.addonsDesc', 'Extend your platform with powerful add-ons. Purchase only what you need, from import/export tools to advanced integrations.')
    }
  ];

  return (
    <PublicLayout>
      <FeaturesSEO />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-purple-600 to-indigo-700 py-20 overflow-hidden">
        <FloatingShapes />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <p className="text-sm font-semibold text-purple-200 uppercase tracking-wide mb-4">{t('footer.features')}</p>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white mb-6">{t('pages.features.title')}</h1>
          <p className="text-xl text-purple-100 max-w-2xl mx-auto">{t('pages.features.subtitle')}</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 -mt-10 relative z-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item, idx) => (
            <div 
              key={item.title} 
              className="group p-8 bg-white border border-gray-100 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 hover:border-purple-200"
            >
              <div className="mb-6 transform group-hover:scale-110 transition-transform duration-300">
                <item.Icon />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900 group-hover:text-purple-700 transition-colors">{item.title}</h3>
              <p className="text-gray-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
        
        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-col sm:flex-row gap-4 items-center justify-center p-8 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl border border-purple-100">
            <div className="text-left">
              <h3 className="text-xl font-bold text-gray-900">{t('pages.features.ctaTitle') || 'Ready to get started?'}</h3>
              <p className="text-gray-600">{t('pages.features.ctaDesc') || 'Create your digital menu in minutes'}</p>
            </div>
            <a 
              href="/signup" 
              className="px-6 py-3 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 transition-colors shadow-lg shadow-purple-200 whitespace-nowrap"
            >
              {t('pages.features.ctaButton') || 'Start for Free'}
            </a>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default Features;
